import { LeafConnector } from '@/components/Quotes/Adders';
import { useSearchProductsMutation } from '@/services/productCatalog';
import { Alert, Box, Button, CheckboxInput, SelectInput, TextInput } from '@palmetto/palmetto-components';
import classNames from 'classnames';
import { FieldArray, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SearchProductDTO, SearchProductsDTO } from 'types';

const equipmentCountOptions = Array.from(Array(10)).map((_val, index) => ({ value: index + 1, label: index + 1 }));

const EquipmentTypeLabelMap: Record<string, string> = {
  backupBattery: 'Backup Battery',
};

interface EquipmentOptionProps {
  equipmentType: string;
  index: number;
  hasActiveQuote: boolean;
  availableEquipmentItems: number;
}
export const EquipmentOption = ({
  equipmentType,
  index,
  hasActiveQuote,
  availableEquipmentItems,
}: EquipmentOptionProps) => {
  const [triggerSearch] = useSearchProductsMutation();

  const { values, setFieldValue } = useFormikContext<any>();
  const [allProducts, setAllProducts] = useState<SearchProductDTO[]>([]);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const label = EquipmentTypeLabelMap[equipmentType] ?? equipmentType;

  const getAllProducts = useCallback(
    async (page = 1, data: SearchProductsDTO = {} as any) => {
      setIsProductsLoading(true);
      try {
        const response = await triggerSearch({
          limit: 20,
          page: page,
          filters: [
            {
              filter: 'classificationPath',
              operation: {
                equals: 'solar/energy-storage/backup',
              },
            },
            {
              filter: 'attribute',
              key: 'lightreach-energy-backup-enabled',
              operation: {
                equals: true,
              },
            },
          ],
        }).unwrap();
        const allData = [...(data?.data || []), ...(response.data || [])];
        if (response.paging?.totalPages && response.paging.page < response.paging.totalPages) {
          await getAllProducts(page + 1, response);
        } else {
          setIsProductsLoading(false);
          setAllProducts(allData);
        }
      } catch (_err: any) {
        setIsProductsLoading(false);
      }
    },
    [triggerSearch],
  );

  useEffect(() => {
    getAllProducts();
  }, [getAllProducts]);

  const manufacturerOptions = useMemo(() => {
    if (allProducts?.length === 0) {
      return [];
    }
    const uniqueManufacturers = new Set<string>();
    for (let i = 0; i < allProducts.length; i++) {
      uniqueManufacturers.add(allProducts[i].manufacturer);
    }
    return Array.from(uniqueManufacturers)
      .toSorted()
      .map((manufacturer: string) => ({
        label: manufacturer,
        value: manufacturer,
      }));
  }, [allProducts]);

  const modelOptions = useMemo(() => {
    if (!values?.equipment?.[index]?.manufacturer || !allProducts?.length) {
      return [];
    }
    return allProducts.reduce<
      { label: string; value: string; productId: string; capacity: number; hasLimitedAvailability?: boolean }[]
    >((acc, product) => {
      if (product.manufacturer !== values?.equipment?.[index]?.manufacturer?.value) {
        return acc;
      }
      const productLine = product.attributes.find((attr: Record<string, any>) => attr.key === 'product-line')
        ?.value as string;
      const capacity = product.attributes.find((attr: Record<string, any>) => attr.key === 'capacity-kwh')
        ?.value as string;
      const hasLimitedAvailability = product.attributes.find(
        (attr: Record<string, any>) => attr.key === 'limited-availability',
      )?.value;
      if (!acc.find((item) => item.value === productLine)) {
        acc.push({
          label: productLine,
          value: productLine,
          productId: product.id,
          capacity: parseFloat(capacity),
          hasLimitedAvailability: hasLimitedAvailability === 'true',
        });
      }
      return acc;
    }, []);
  }, [values?.equipment, index, allProducts]);

  return (
    <Box direction="row" wrap gap="lg">
      <Box flex="auto" direction="column" gap="md">
        <CheckboxInput
          id={equipmentType}
          name={equipmentType}
          label={label}
          isDisabled={hasActiveQuote || availableEquipmentItems === 1}
          onChange={(event) => {
            if (event.target.checked) {
              setFieldValue(`equipment.${index}`, {
                type: equipmentType,
                manufacturer: '',
                models: [{ name: '', quantity: { label: '1', value: 1 } }],
              });
            } else {
              setFieldValue(`equipment.${index}`, undefined);
            }
          }}
          isChecked={values?.equipment?.[index]?.type === equipmentType}
        />
        {values?.equipment?.[index]?.type === equipmentType && (
          <Box childGap="lg">
            <Box direction="row" padding="0 0 0 sm">
              <LeafConnector />
              <Box direction={{ base: 'column', tablet: 'row' }} gap="lg" style={{ flex: 'auto' }}>
                <Box gap="lg" style={{ flex: '1' }}>
                  <SelectInput
                    label="Manufacturer"
                    name={`${equipmentType}Manufacturer`}
                    id={`${equipmentType}Manufacturer`}
                    value={values?.equipment?.[index]?.manufacturer}
                    options={manufacturerOptions}
                    isDisabled={isProductsLoading || hasActiveQuote}
                    isLoading={isProductsLoading}
                    isRequired
                    onChange={(event) => {
                      setFieldValue(`equipment.${index}`, {
                        ...values?.equipment?.[index],
                        manufacturer: event.target.value,
                        models: [{ name: '', quantity: { label: '1', value: 1 }, capacity: 0 }],
                      });
                    }}
                  />
                  {values?.equipment?.[index]?.models[index]?.hasLimitedAvailability && (
                    <Alert
                      hasIcon
                      variant="warning"
                      message="The equipment selected requires pre-approval from Palmetto LightReach. Please reach out to your RM to obtain approval before proceeding with this system design."
                    />
                  )}
                </Box>
                <FieldArray
                  name={`${equipmentType}Models`}
                  render={() => (
                    <Box style={{ flex: '1' }} childGap="lg">
                      {values?.equipment?.[index]?.models?.map(
                        (
                          model: {
                            quantity: { value: number; label: string };
                            name: { label: string; value: string };
                            capacity: number;
                          },
                          modelIndex: number,
                        ) => (
                          <Box key={modelIndex} direction="row" style={{ flex: '1' }} gap="lg">
                            {modelIndex > 0 && (
                              <Button
                                variant="secondary"
                                tone="neutral"
                                disabled={hasActiveQuote}
                                onClick={() => {
                                  setFieldValue(`equipment.${index}`, {
                                    ...values?.equipment,
                                    models: values?.equipment?.[index]?.models?.filter(
                                      (_: unknown, i: number) => i !== modelIndex,
                                    ),
                                  });
                                }}
                                iconPrefix="remove"
                                size="sm"
                                style={{
                                  position: 'absolute',
                                  transform: 'translate(-60px, 25px)',
                                  padding: '10px',
                                }}
                              />
                            )}
                            <Box style={{ flex: '1' }}>
                              <SelectInput
                                label="Model"
                                name={`${equipmentType}Model`}
                                id={`${equipmentType}Model`}
                                value={model.name}
                                options={modelOptions}
                                isDisabled={isProductsLoading || hasActiveQuote}
                                isRequired
                                onChange={(event) => {
                                  const existingModelData = values?.equipment?.[index]?.models[modelIndex] || {
                                    name: event.target.value,
                                    quantity: { label: '1', value: 1 },
                                    productId: event.target.value.productId,
                                    capacity: event.target.value.capacity,
                                    hasLimitedAvailability: event.target.value.hasLimitedAvailability,
                                  };
                                  const { productId, capacity, hasLimitedAvailability, ...rest } =
                                    event.target.value;
                                  const updatedModelData = {
                                    ...existingModelData,
                                    name: rest,
                                    productId,
                                    capacity,
                                    hasLimitedAvailability,
                                  };
                                  const updatedModels = [
                                    ...(values?.equipment?.[index]?.models.slice(0, modelIndex) || []),
                                    updatedModelData,
                                    ...(values?.equipment?.[index]?.models.slice(modelIndex + 1) || []),
                                  ];
                                  setFieldValue(`equipment.${index}`, {
                                    ...values?.equipment?.[index],
                                    models: updatedModels,
                                  });
                                }}
                              />
                            </Box>
                            <Box>
                              <SelectInput
                                label="Quantity"
                                name={`${equipmentType}Quantity`}
                                id={`${equipmentType}Quantity`}
                                options={equipmentCountOptions}
                                value={model.quantity}
                                isDisabled={isProductsLoading || hasActiveQuote}
                                isRequired
                                onChange={(event) => {
                                  const existingModelData = values?.equipment?.[index]?.models[modelIndex] || {
                                    name: '',
                                  };
                                  const updatedModelData = {
                                    ...existingModelData,
                                    quantity: event.target.value,
                                  };
                                  const updatedModels = [
                                    ...(values?.equipment?.[index]?.models.slice(0, modelIndex) || []),
                                    updatedModelData,
                                    ...(values?.equipment?.[index]?.models.slice(modelIndex + 1) || []),
                                  ];
                                  setFieldValue(`equipment.${index}`, {
                                    ...values?.equipment?.[index],
                                    models: updatedModels,
                                  });
                                }}
                              />
                            </Box>
                          </Box>
                        ),
                      )}
                      <Box />
                    </Box>
                  )}
                />
              </Box>
            </Box>
            {modelOptions?.length > 1 && (
              <Button
                onClick={() => {
                  setFieldValue(`equipment.${index}`, {
                    ...values?.equipment?.[index],
                    models: [
                      ...(values?.equipment?.[index]?.models || []),
                      { name: '', quantity: { label: '1', value: 1 }, capacity: 0 },
                    ],
                  });
                }}
                variant="secondary"
                tone="neutral"
                size="sm"
                iconPrefix="add"
                className={classNames({
                  'shadow-xs': true,
                })}
                isDisabled={hasActiveQuote}
                aria-label="add battery"
                style={{ width: 'fit-content', marginLeft: 'auto' }}
              >
                Add {label}
              </Button>
            )}
            <Box margin="md 0 0 sm" alignSelf="flex-end">
              <TextInput
                id="required"
                value={values?.equipment?.[index]?.totalCost}
                type="number"
                label={`${label} Total Cost`}
                prefix="$"
                placeholder="$ e.g. 10000"
                onChange={(event) =>
                  setFieldValue(`equipment.${index}`, {
                    ...values?.equipment?.[index],
                    totalCost: parseFloat(event.target.value),
                  })
                }
                isRequired
                maxWidth="2xl"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
